import { useRef, useState } from "react";
import { supabase } from "../lib/supabase";

type Kind = "wordmark" | "wordmark_reverse" | "icon";

const SPEC: Record<Kind, { title: string; size: string; hint: string; limitKb: number }> = {
  wordmark: {
    title: "Wordmark",
    size: "720 × 240px",
    hint: "Shown in the sidebar and on the sign-in screen at 28px tall. 720px wide keeps it sharp on high-resolution screens.",
    limitKb: 500,
  },
  wordmark_reverse: {
    title: "Wordmark for dark backgrounds",
    size: "720 × 240px",
    hint: "Optional. Without it we show the standard mark on a light plate in dark mode rather than guessing at colours.",
    limitKb: 500,
  },
  icon: {
    title: "Square mark",
    size: "512 × 512px",
    hint: "Used in the browser tab and anywhere the wordmark will not fit.",
    limitKb: 250,
  },
};

export function LogoUpload({
  companyId, kind, currentKey, onDone,
}: { companyId: string; kind: Kind; currentKey: string | null; onDone: () => void }) {
  const spec = SPEC[kind];
  const input = useRef<HTMLInputElement>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function upload(file: File) {
    setError(null);

    // Checked here as well as in the Worker, so the person gets the reason
    // immediately rather than after uploading half a megabyte.
    if (file.type === "image/jpeg") {
      setError("JPEG has no transparent background — export as PNG or SVG, or the logo will sit on a white rectangle.");
      return;
    }
    if (!["image/png", "image/svg+xml", "image/webp"].includes(file.type)) {
      setError("Use PNG, SVG or WebP.");
      return;
    }
    if (file.size > spec.limitKb * 1024) {
      setError(`That file is ${Math.round(file.size / 1024)}KB. The limit is ${spec.limitKb}KB — export it smaller.`);
      return;
    }

    setBusy(true);
    const { data: sess } = await supabase.auth.getSession();
    const res = await fetch(
      `${import.meta.env.VITE_API_URL}/branding/${companyId}/${kind}`,
      {
        method: "POST",
        headers: {
          "Content-Type": file.type,
          Authorization: `Bearer ${sess.session?.access_token ?? ""}`,
        },
        body: file,
      },
    );
    setBusy(false);
    if (!res.ok) {
      const body = (await res.json().catch(() => ({}))) as { error?: string };
      setError(body.error ?? "That upload did not work.");
      return;
    }
    onDone();
  }

  return (
    <div className="logo-slot">
      <div className="logo-preview">
        {currentKey
          ? <img src={`${import.meta.env.VITE_API_URL}/branding/${currentKey}`} alt="" />
          : <span className="muted small">Nothing uploaded</span>}
      </div>
      <div>
        <b>{spec.title}</b>
        <p className="muted small">{spec.size} · PNG, SVG or WebP · under {spec.limitKb}KB</p>
        <p className="muted small">{spec.hint}</p>
        <input ref={input} type="file" accept="image/png,image/svg+xml,image/webp" hidden
          onChange={(e) => { const f = e.target.files?.[0]; if (f) void upload(f); }} />
        <button className="btn ghost small" disabled={busy}
          onClick={() => input.current?.click()}>
          {busy ? "Uploading…" : currentKey ? "Replace" : "Upload"}
        </button>
        {error && <p className="status error small">{error}</p>}
      </div>
    </div>
  );
}
